import { Button } from '@visikon/spine/src';
import { tracker } from '@visikon/tracker/src';
import { WindowSize, useWindowSizeMatches } from '@visikon/utils/src/responsive';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactToPrint from 'react-to-print';
import styled from 'styled-components';
import { setIcons } from '../../actions/resourcesActions';
import { ContentContainer } from '../../components/ContentContainerSection';
import { WithFooter } from '../../components/Footer';
import { SideBarContainer } from '../../components/SideBarContainer';
import { DocumentRenderer } from '../../components/documentViewer/DocumentRenderer';
import { EditButton } from '../../components/edit/EditButton';
import { PopupContent } from '../../components/popup/PopupContent';
import { FlexWrapper, SideBarContent } from '../../components/styled/ContentAreaContainers';
import { ReactComponent as tocIcon } from '../../images/svg/toc.svg';
import { SvgIcon } from '../../images/svgIcon';
import { I18N, SetDocumentTitle } from '../../internationalization/AppText';
import { i18n } from '../../internationalization/i18n';
import { DocumentJSON } from '../../types/slate-types';
import { MarkdownRenderer } from '../contactPopup/MarkdownRenderer';
import { useSelfServiceUnsavedChanges } from '../embeddedMessageScript/EmbeddedMessageScript';
import { LinkButton, Spacer } from './PhamletView.styles';
import { DocumentWrapper, PrintButtonWrapper } from './PhampletSharedComponents';
import { TableOfContentComponent } from './TableOfContentComponent';

interface IProps {
  documentId: string;
  language: string;
  text?: DocumentJSON;
  title?: string;
  index?: number;
}

const Space = styled.div<{ height: number }>`
  min-height: ${(p) => p.height}px;
`;

export const PhampletView = ({ documentId, language, text: originalText, title, index }: IProps) => {
  const docContainerRef = useRef<HTMLDivElement>(null);
  const documentWrapper = useRef<HTMLDivElement>(null);
  const [tableOfContent, setTableOfContent] = useState<string[]>();
  const [clicked, setClicked] = useState(false);
  const [printing, setPrinting] = useState(false);
  const smallWindow = useWindowSizeMatches(WindowSize.Medium);
  const dispatch = useDispatch();
  const onTocClick = (key) => {
    setClicked(!clicked);
  };

  const unsavedChanges = useSelfServiceUnsavedChanges();

  const [text, setText] = useState<DocumentJSON | undefined>(originalText);

  useEffect(() => {
    const unsavedPamphlet = unsavedChanges.operations.find((op) => op.contentType === 'phamplets' && op.data._id === documentId);
    const newTextElement = unsavedPamphlet?.data?.translations?.find((t) => t.language === language)?.content;
    if (newTextElement && newTextElement.document) {
      setText(newTextElement.document as DocumentJSON);
    }
  }, [unsavedChanges]);

  const icon = (
    <LinkButton onClick={onTocClick} style={{ paddingRight: 12 }} aria-label={i18n('table_of_content')}>
      <SvgIcon aria-hidden Icon={tocIcon} width={24} />
    </LinkButton>
  );
  const hasToc = tableOfContent && Object.keys(tableOfContent).length > 1;
  // @ts-ignore
  useEffect(() => {
    if (hasToc && smallWindow) {
      // @ts-ignore
      dispatch(setIcons([icon]));
    } else {
      dispatch(setIcons(undefined));
    }
    return () => {
      dispatch(setIcons(undefined));
    };
  }, [hasToc, smallWindow]);

  const toc = (
    <TableOfContentComponent tocElementClicked={onTocClick} tableOfContent={tableOfContent} docContainer={docContainerRef.current || undefined} />
  );

  const handlePrint = () => {
    setPrinting(true);
    tracker.trackEvent('Phamplets', 'printClicked', title);
  };

  if (text) {
    const printButton = (
      <PrintButtonWrapper>
        <Button variant="outline" onClick={handlePrint}>
          <I18N text="print_document" />
        </Button>
      </PrintButtonWrapper>
    );
    return (
      <FlexWrapper ref={documentWrapper}>
        <SetDocumentTitle text="" textNoTranslation={`${i18n('pamphlets')} - ${title}`} />
        <ContentContainer ref={docContainerRef}>
          <DocumentWrapper>
            <WithFooter>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px' }}>
                <EditButton currentIndex={index} contentType="phamplets" />
                <ReactToPrint trigger={() => printButton} content={() => documentWrapper.current} onAfterPrint={() => setPrinting(false)} />
              </div>
              {text.data?.markdown ? (
                <MarkdownRenderer content={text.data.markdown} getTableOfContent={setTableOfContent} />
              ) : (
                <DocumentRenderer document={text} getTableOfContent={setTableOfContent} />
              )}
            </WithFooter>
          </DocumentWrapper>
          <Spacer $height={40} />

          {smallWindow && hasToc && clicked && (
            <PopupContent handleOverlayClick={() => onTocClick(null)} positionLeft={10} positionTop={10} width={450} content={toc} />
          )}
        </ContentContainer>

        {!smallWindow && hasToc && !printing ? (
          <SideBarContainer>
            <SideBarContent>{toc}</SideBarContent>
          </SideBarContainer>
        ) : null}
      </FlexWrapper>
    );
  }

  return null;
};
